import { useEffect, useRef, useState } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useSpring, animated } from '@react-spring/three';
import { useFrame } from "@react-three/fiber";
import { Center, Text3D } from "@react-three/drei";
import { useParams } from "wouter";
import * as THREE from 'three';

import layout from "../../dictionaries/layout.js";
import translations from "../../dictionaries/translations.js";
import { getSocket } from "../../socket.js";
import { containsChineseCharacters, formatName, getNameFont } from "../../logicHelpers/helpers.js";
import { trackEvent } from '../../api';

import Star from "../../meshes/Stars/Star.jsx";
import Rocket from "../../meshes/Rocket.jsx";
import Ufo from "../../meshes/Ufo.jsx";
import SeatStar from "../../meshes/Stars/SeatStar.jsx";
import YouStars from "../YouStars.jsx";
import BlueMoon from "../../meshes/BlueMoon.jsx";
import YootDisplay from "../YootDisplay.jsx";
import JoinTeamModal from "../JoinTeamModal.jsx";
import PlayerNameModal from "../PlayerNameModal.jsx";
import TeamNameModal from "../TeamNameModal.jsx";
import LanguageModalAnimated from "./LanguageModalAnimated.jsx";
import useSoundEffectsPlayer from "../../soundPlayers/useSoundEffectsPlayer.jsx";

import { 
  deviceAtom, 
  hostAtom, 
  clientAtom,
  joinTeamAtom,
  teamsAtom,
  guestBeingEdittedAtom,
  seatChosenAtom,
  audioVolumeAtom,
  languageAtom,
  languageModalOpenAtom,
} from "../../GlobalState.jsx";

const socket = getSocket();

function LobbyPlayerName({ name, fallbackFont, position, size, color }) {
  const text = formatName(name, 15)

  return <Text3D
    font={containsChineseCharacters(text) ? '/fonts/Xiao Xiangjiao font_Regular.json' : getNameFont(name, fallbackFont)}
    position={position}
    rotation={[-Math.PI / 2, 0, 0]}
    size={size}
    height={0.01}
    lineHeight={0.7}
  >
    {text}
    <meshStandardMaterial color={color} />
  </Text3D>
}

export default function PlayersParty({ position=[0,0,0], scale=0.7 }) {
  const device = useAtomValue(deviceAtom)
  const params = useParams();
  const host = useAtomValue(hostAtom)
  const client = useAtomValue(clientAtom)  
  const teams = useAtomValue(teamsAtom)
  const [guestBeingEditted, setGuestBeingEditted] = useAtom(guestBeingEdittedAtom)
  const [seatChosen, setSeatChosen] = useAtom(seatChosenAtom)
  const joinTeam = useAtomValue(joinTeamAtom)
  const languageModalOpen = useAtomValue(languageModalOpenAtom)
  const setLanguageModalOpen = useSetAtom(languageModalOpenAtom)
  const [language, setLanguage] = useAtom(languageAtom)
  const { playSoundEffect } = useSoundEffectsPlayer()
  const [editingTeamName, setEditingTeamName] = useState(null)
  const [editingPlayerName, setEditingPlayerName] = useState(false)

  function LobbyTeamTitle({ team, position, rightEdge }) {
    const [titleWidth, setTitleWidth] = useState(0)
    const [hovered, setHovered] = useState(false)
    const teamName = teams[team].name || (team === 0 ? translations.lobby.teamRocket[language] : translations.lobby.teamUfo[language])
    const teamColor = team === 0 ? 'red' : 'turquoise'
    const titlePosition = [rightEdge === undefined ? position[0] : rightEdge - titleWidth, position[1], position[2]]
    const shipsCenterX = titlePosition[0] + titleWidth / 2
    const canEditTeamName = client.team === team

    function handleTitlePointerEnter(event) {
      if (!canEditTeamName) return
      event.stopPropagation()
      setHovered(true)
      document.body.style.cursor = 'pointer'
    }

    function handleTitlePointerLeave(event) {
      if (!canEditTeamName) return
      event.stopPropagation()
      setHovered(false)
      document.body.style.cursor = 'default'
    }

    function handleTitlePointerDown(event) {
      if (!canEditTeamName) return
      event.stopPropagation()
      setEditingTeamName(team)
      trackEvent({ surface: 'lobby', action: 'edit_team_name', category: 'ui', metadata: { team } })
    }

    return <>
      <mesh
        position={[titlePosition[0] + titleWidth / 2, position[1] - 0.03, position[2] - 0.18]}
        onPointerEnter={handleTitlePointerEnter}
        onPointerLeave={handleTitlePointerLeave}
        onPointerDown={handleTitlePointerDown}
      >
        <boxGeometry args={[Math.max(1, titleWidth + 0.35), 0.04, 0.75]} />
        <meshStandardMaterial color={teamColor} transparent opacity={0} />
      </mesh>
      <Text3D
        font={getNameFont(teamName, language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json")}
        position={titlePosition}
        rotation={[-Math.PI/2, 0, 0]}
        size={0.5}
        height={0.01}
        lineHeight={0.7}
        onUpdate={(text) => {
          text.geometry.computeBoundingBox()
          const boundingBox = text.geometry.boundingBox
          const width = boundingBox ? boundingBox.max.x - boundingBox.min.x : 0
          setTitleWidth((currentWidth) => Math.abs(currentWidth - width) < 0.01 ? currentWidth : width)
        }}
      >
        {teamName}
        <meshStandardMaterial color={teamColor} />
      </Text3D>
      {[-1.12, -0.44, 0.24, 0.92].map((offset) => (
        team === 0
          ? <Rocket key={offset} position={[shipsCenterX + offset, position[1], position[2] + 0.52]} scale={0.9} />
          : <Ufo key={offset} position={[shipsCenterX + offset + 0.5, position[1], position[2] + 0.52]} scale={0.9} />
      ))}
    </>
  }

  const { partyScale, joinTeamScale, teamNameScale, playerNameScale, seatScale, editGuestScale } = useSpring({
    partyScale: (joinTeam === null && editingTeamName === null && !editingPlayerName && !seatChosen && !guestBeingEditted && !languageModalOpen) ? 1 : 0,
    joinTeamScale: joinTeam !== null ? 1 : 0,
    teamNameScale: editingTeamName !== null ? 1 : 0,
    playerNameScale: editingPlayerName ? 1 : 0,
    seatScale: seatChosen ? 1 : 0,
    editGuestScale: guestBeingEditted ? 1 : 0,
    config: { tension: 170, friction: 26 }
  })

  // #region animation
  const partyRef = useRef()
  const rocket0 = useRef()
  const rocket1 = useRef()
  const rocket2 = useRef()
  const rocket3 = useRef()
  const ufo0 = useRef()
  const ufo1 = useRef()
  const ufo2 = useRef()
  const ufo3 = useRef()
  const shipRefs = [rocket0, rocket1, rocket2, rocket3, ufo0, ufo1, ufo2, ufo3]
  const radius = 1.6
  const radius2 = 10.5
  const spring = useSpring({
    from: {
      boomScale: 0.6,
      boomScaleYut: 0.14
    },
    to: [
      {
        boomScale: 0.603,
        boomScaleYut: 0.145,
        config: {
          tension: 500,
          clamp: true
        }
      },
      {
        boomScale: 0.6,
        boomScaleYut: 0.14,
      },
    ],
    loop: true,
    delay: 234
  })
  useFrame((state) => {
    const time = state.clock.elapsedTime
    // partyRef.current.rotation.y = -time / 4;
    if (shipRefs[0].current) {
      shipRefs.forEach((value, index) => {
        if (value.current) {
          if (device === 'portrait') {
            value.current.position.x = Math.cos(-time / 3 + (Math.PI * 2 / shipRefs.length * index)) * radius2
            value.current.position.y = 5
            value.current.position.z = Math.sin(-time / 3 + (Math.PI * 2 / shipRefs.length * index)) * radius2 + 3.5
          } else {
            value.current.position.x = Math.cos((Math.PI * 2 / shipRefs.length * (index-1.5))) * radius2
            value.current.position.y = 5
            value.current.position.z = Math.sin((Math.PI * 2 / shipRefs.length * (index-1.5))) * radius2 + 2
          }
        }
      })
    }
  })
  // #endregion

  const setJoinTeam = useSetAtom(joinTeamAtom)

  function Seats({ position, scale }) {
    const teams = useAtomValue(teamsAtom)
    
    // Helper function to determine seat font size
    const getSeatFontSize = (seatTeam, seatIndex) => {
      
      // 2. If the seat is occupied by another player, size should be 0.7
      if (teams[seatTeam].players[seatIndex]) {
        return 0.7;
      }
      
      // 4. Otherwise (empty seat on opposite team), size should be 0.5
      return 0.5;
    };
    
    // #region Seat hover
    const [seat1Team0Hover, setSeat1Team0Hover] = useState(false)
    const [seat2Team0Hover, setSeat2Team0Hover] = useState(false)
    const [seat3Team0Hover, setSeat3Team0Hover] = useState(false)
    const [seat4Team0Hover, setSeat4Team0Hover] = useState(false)
    const [seat1Team1Hover, setSeat1Team1Hover] = useState(false)
    const [seat2Team1Hover, setSeat2Team1Hover] = useState(false)
    const [seat3Team1Hover, setSeat3Team1Hover] = useState(false)
    const [seat4Team1Hover, setSeat4Team1Hover] = useState(false)
    const [audioVolume, setAudioVolume] = useAtom(audioVolumeAtom)

    function handleSeat1Team0PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat1Team0Hover(true)
    }
    function handleSeat1Team0PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat1Team0Hover(false)
    }
    function handleSeat2Team0PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat2Team0Hover(true)
    }
    function handleSeat2Team0PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat2Team0Hover(false)
    }
    function handleSeat3Team0PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat3Team0Hover(true)
    }
    function handleSeat3Team0PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat3Team0Hover(false)
    }
    function handleSeat4Team0PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat4Team0Hover(true)
    }
    function handleSeat4Team0PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat4Team0Hover(false)
    }
    function handleSeat1Team1PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat1Team1Hover(true)
    }
    function handleSeat1Team1PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat1Team1Hover(false)
    }
    function handleSeat2Team1PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat2Team1Hover(true)
    }
    function handleSeat2Team1PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat2Team1Hover(false)
    }
    function handleSeat3Team1PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat3Team1Hover(true)
    }
    function handleSeat3Team1PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat3Team1Hover(false)
    }
    function handleSeat4Team1PointerEnter(e) {
      e.stopPropagation()
      document.body.style.cursor = 'pointer'
      setSeat4Team1Hover(true)
    }
    function handleSeat4Team1PointerLeave(e) {
      e.stopPropagation()
      document.body.style.cursor = 'default'
      setSeat4Team1Hover(false)
    }
    // #endregion

    function handleSeatPointerDown(e, team, seatIndex) {
      e.stopPropagation()
      setLanguageModalOpen(false)

      const clickedPlayer = teams[team].players[seatIndex];
      if (clickedPlayer && clickedPlayer.socketId === client.socketId) {
        setEditingPlayerName(true)
        return;
      }

      if (client.socketId === host.socketId) {
        if (client.team !== team && !teams[team].players[seatIndex]) {
          setSeatChosen([team, seatIndex])
        } else if (teams[team].players[seatIndex] && teams[team].players[seatIndex].socketId !== client.socketId) {
          const player = teams[team].players[seatIndex]
          setGuestBeingEditted({
            name: player.name,
            connectionState: player.connectedToRoom,
            isYou: false,
            isHost: false,
            team: player.team,
            status: player.status,
            type: player.type,
            _id: player._id,
          })
        } else {
          setSeatChosen([team, seatIndex])
        }
      } else if (client.team !== team) {
        setJoinTeam(team)
      }
      
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }

    // Players are in seat by their index in the teams[team].players array
    return <group position={position} scale={scale}>
      <animated.group 
      name='rocket-seat-0'
      scale={0.6} 
      position={[
        -Math.cos(Math.PI * 2 / 8 + Math.PI/8) * radius, 
        5, 
        -Math.sin(Math.PI * 2 / 8 + Math.PI/8) * radius - 0.5
      ]}>
        { teams[0].players[0] && host.socketId === teams[0].players[0].socketId && <Star scale={0.45} position={[0, 0, 0]} color='orange'/> }
        { teams[0].players[0] && client.socketId === teams[0].players[0].socketId && <YouStars team={0} position={[-7, 0, -0.5]}/> }
        { !teams[0].players[0] && <SeatStar colorStart='#ffff00' colorFinish='#bc7a00' scale={client.team === -1 ? 0.6 : 0.3}/> }
        <LobbyPlayerName
          name={teams[0].players[0] ? teams[0].players[0].name : translations.lobby.clickToSit[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-6.7, 0.02, 0.3]}
          size={getSeatFontSize(0, 0)}
          color={(teams[0].players[0] && !teams[0].players[0].connectedToRoom) ? 'grey' : seat1Team0Hover ? 'orange' : 'red'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[-3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat1Team0Hover ? 'orange' : 'red' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[-3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
            name='wrapper' 
            position={[-3.2, 0, 0]} 
            scale={[7.8, 0.02, 1.3]}
            onPointerEnter={e => handleSeat1Team0PointerEnter(e)}
            onPointerLeave={e => handleSeat1Team0PointerLeave(e)}
            onPointerDown={e => handleSeatPointerDown(e, 0, 0)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='rocket-seat-1'
      scale={0.6} 
      position={[
        -Math.cos(Math.PI * 2 / 8 - Math.PI/8) * radius, 
        5, 
        -Math.sin(Math.PI * 2 / 8 - Math.PI/8) * radius
      ]}>
        { teams[0].players[1] && host.socketId === teams[0].players[1].socketId && <Star scale={0.45} position={[0, 0, 0]} color='orange'/> }
        { teams[0].players[1] && client.socketId === teams[0].players[1].socketId && <YouStars team={0} position={[-7, 0, -0.5]}/> }
        { !teams[0].players[1] && <SeatStar colorStart='#ffff00' colorFinish='#bc7a00' scale={client.team === -1 ? 0.6 : 0.3} /> }
        <LobbyPlayerName
          name={teams[0].players[1] ? teams[0].players[1].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-6.7, 0.02, 0.3]}
          size={getSeatFontSize(0, 1)}
          color={(teams[0].players[1] && !teams[0].players[1].connectedToRoom) ? 'grey' : seat2Team0Hover ? 'orange' : 'red'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[-3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat2Team0Hover ? 'orange' : 'red' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[-3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[-3.2, 0, 0]} 
          scale={[7.8, 0.02, 1.3]}
          onPointerEnter={e => handleSeat2Team0PointerEnter(e)}
          onPointerLeave={e => handleSeat2Team0PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 0, 1)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='rocket-seat-2'
      scale={0.6} 
      position={[
        -Math.cos(0 - Math.PI/8) * radius, 
        5, 
        -Math.sin(0 - Math.PI/8) * radius + 0.1
      ]}>
        { teams[0].players[2] && host.socketId === teams[0].players[2].socketId && <Star scale={0.45} position={[0, 0, 0]} color='orange'/> }
        { teams[0].players[2] && client.socketId === teams[0].players[2].socketId && <YouStars team={0} position={[-7, 0, -0.5]}/> }
        { !teams[0].players[2] && <SeatStar colorStart='#ffff00' colorFinish='#bc7a00' scale={client.team === -1 ? 0.6 : 0.3}/> }
        <LobbyPlayerName
          name={teams[0].players[2] ? teams[0].players[2].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-6.7, 0.02, 0.3]}
          size={getSeatFontSize(0, 2)}
          color={(teams[0].players[2] && !teams[0].players[2].connectedToRoom) ? 'grey' : seat3Team0Hover ? 'orange' : 'red'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[-3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat3Team0Hover ? 'orange' : 'red' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[-3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[-2.2, 0, 0]} 
          scale={[7.8, 0.02, 1.5]}
          onPointerEnter={e => handleSeat3Team0PointerEnter(e)}
          onPointerLeave={e => handleSeat3Team0PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 0, 2)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='rocket-seat-3'
      scale={0.6} 
      position={[
        -Math.cos(-Math.PI * 2 / 8 - Math.PI/8) * radius - 0.2, 
        5, 
        -Math.sin(-Math.PI * 2 / 8 - Math.PI/8) * radius + 0.6
      ]}>
        { teams[0].players[3] && host.socketId === teams[0].players[3].socketId && <Star scale={0.45} position={[0, 0, 0]} color='orange'/> }
        { teams[0].players[3] && client.socketId === teams[0].players[3].socketId && <YouStars team={0} position={[-7, 0, -0.5]}/> }
        { !teams[0].players[3] && <SeatStar colorStart='#ffff00' colorFinish='#bc7a00' scale={client.team === -1 ? 0.6 : 0.3}/> }
        <LobbyPlayerName
          name={teams[0].players[3] ? teams[0].players[3].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-6.7, 0.02, 0.3]}
          size={getSeatFontSize(0, 3)}
          color={(teams[0].players[3] && !teams[0].players[3].connectedToRoom) ? 'grey' : seat4Team0Hover ? 'orange' : 'red'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[-3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat4Team0Hover ? 'orange' : 'red' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[-3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[-3.2, 0, 0]} 
          scale={[7.8, 0.02, 1.5]}
          onPointerEnter={e => handleSeat4Team0PointerEnter(e)}
          onPointerLeave={e => handleSeat4Team0PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 0, 3)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='ufo-seat-0'
      scale={0.6} 
      position={[
        Math.cos(Math.PI * 2 / 8 + Math.PI/8) * radius, 
        5, 
        -Math.sin(Math.PI * 2 / 8 + Math.PI/8) * radius - 0.5
      ]}>
        { teams[1].players[0] && host.socketId === teams[1].players[0].socketId && <Star scale={0.45} position={[6.4, 0, 0]} color='orange'/> }
        { teams[1].players[0] && client.socketId === teams[1].players[0].socketId && <YouStars team={1} position={[-0.5, 0, -0.5]}/> }
        { !teams[1].players[0] && <SeatStar colorStart='#ffff00' colorFinish='turquoise' scale={client.team === -1 ? 0.6 : 0.3} position={[6.4, 0, 0]}/> }
        <LobbyPlayerName
          name={teams[1].players[0] ? teams[1].players[0].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-0.3, 0.02, 0.3]}
          size={getSeatFontSize(1, 0)}
          color={(teams[1].players[0] && !teams[1].players[0].connectedToRoom) ? 'grey' : seat1Team1Hover ? 'green' : 'turquoise'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat1Team1Hover ? 'green' : 'turquoise' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.02, 1.5]}
          onPointerEnter={e => handleSeat1Team1PointerEnter(e)}
          onPointerLeave={e => handleSeat1Team1PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 1, 0)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='ufo-seat-1'
      scale={0.6} 
      position={[
        Math.cos(Math.PI * 2 / 8 - Math.PI/8) * radius, 
        5, 
        -Math.sin(Math.PI * 2 / 8 - Math.PI/8) * radius
      ]}>
        { teams[1].players[1] && host.socketId === teams[1].players[1].socketId && <Star scale={0.45} position={[6.4, 0, 0]} color='orange'/> }
        { teams[1].players[1] && client.socketId === teams[1].players[1].socketId && <YouStars team={1} position={[-0.5, 0, -0.5]}/>}
        { !teams[1].players[1] && <SeatStar colorStart='#ffff00' colorFinish='turquoise' scale={client.team === -1 ? 0.6 : 0.3} position={[6.4, 0, 0]}/> }
        <LobbyPlayerName
          name={teams[1].players[1] ? teams[1].players[1].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-0.3, 0.02, 0.3]}
          size={getSeatFontSize(1, 1)}
          color={(teams[1].players[1] && !teams[1].players[1].connectedToRoom) ? 'grey' : seat2Team1Hover ? 'green' : 'turquoise'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat2Team1Hover ? 'green' : 'turquoise' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.02, 1.5]}
          onPointerEnter={e => handleSeat2Team1PointerEnter(e)}
          onPointerLeave={e => handleSeat2Team1PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 1, 1)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='ufo-seat-2'
      scale={0.6} 
      position={[
        Math.cos(0 - Math.PI/8) * radius, 
        5, 
        -Math.sin(0 - Math.PI/8) * radius + 0.1
      ]}>
        { teams[1].players[2] && host.socketId === teams[1].players[2].socketId && <Star scale={0.45} position={[6.4, 0, 0]} color='orange'/> }
        { teams[1].players[2] && client.socketId === teams[1].players[2].socketId && <YouStars team={1} position={[-0.5, 0, -0.5]}/>}
        { !teams[1].players[2] && <SeatStar colorStart='#ffff00' colorFinish='turquoise' scale={client.team === -1 ? 0.6 : 0.3} position={[6.4, 0, 0]}/> }
        <LobbyPlayerName
          name={teams[1].players[2] ? teams[1].players[2].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-0.3, 0.02, 0.3]}
          size={getSeatFontSize(1, 2)}
          color={(teams[1].players[2] && !teams[1].players[2].connectedToRoom) ? 'grey' : seat3Team1Hover ? 'green' : 'turquoise'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat3Team1Hover ? 'green' : 'turquoise' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.02, 1.5]}
          onPointerEnter={e => handleSeat3Team1PointerEnter(e)}
          onPointerLeave={e => handleSeat3Team1PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 1, 2)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
      <animated.group 
      name='ufo-seat-3'
      scale={0.6} 
      position={[
        Math.cos(-Math.PI * 2 / 8 - Math.PI/8) * radius + 0.2, 
        5, 
        -Math.sin(-Math.PI * 2 / 8 - Math.PI/8) * radius + 0.6
      ]}>
        { teams[1].players[3] && host.socketId === teams[1].players[3].socketId && <Star scale={0.45} position={[6.4, 0, 0]} color='orange'/> }
        { teams[1].players[3] && client.socketId === teams[1].players[3].socketId && <YouStars team={1} position={[-0.5, 0, -0.5]}/>}
        { !teams[1].players[3] && <SeatStar colorStart='#ffff00' colorFinish='turquoise' scale={client.team === -1 ? 0.6 : 0.3} position={[6.4, 0, 0]}/> }
        <LobbyPlayerName
          name={teams[1].players[3] ? teams[1].players[3].name : client.team === -1 ? translations.lobby.clickToSit[language] : translations.lobby.openSeat[language]}
          fallbackFont={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-0.3, 0.02, 0.3]}
          size={getSeatFontSize(1, 3)}
          color={(teams[1].players[3] && !teams[1].players[3].connectedToRoom) ? 'grey' : seat4Team1Hover ? 'green' : 'turquoise'}
        />
        <group name='background'>
          <mesh 
          name='background-outer' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.01, 1.5]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color={ seat4Team1Hover ? 'green' : 'turquoise' } transparent opacity={0.1}/>
          </mesh>
          <mesh 
          name='background-inner' 
          position={[3.2, 0, 0]} 
          scale={[7.7, 0.02, 1.4]}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0.3}/>
          </mesh>
          <mesh 
          name='wrapper' 
          position={[3.2, 0, 0]} 
          scale={[7.8, 0.02, 1.5]}
          onPointerEnter={e => handleSeat4Team1PointerEnter(e)}
          onPointerLeave={e => handleSeat4Team1PointerLeave(e)}
          onPointerDown={e => handleSeatPointerDown(e, 1, 3)}
          >
            <boxGeometry args={[1, 1, 1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
      </animated.group>
    </group>
  }

  function mapTeamToPlayerColor(team) {
    if (team === -1) {
      return '#9F9F9F'
    } else if (team === 0) {
      return 'red'
    } else if (team === 1) {
      return 'turquoise'
    }
  }

  // -1 team: spectator
  function EditOneGuest({ position=[0,0,0], scale=1 }) {  
    const [lastGuestBeingEditted, setLastGuestBeingEditted] = useState(guestBeingEditted)
    useEffect(() => {
      if (guestBeingEditted) setLastGuestBeingEditted(guestBeingEditted)
    }, [guestBeingEditted])

    if (!lastGuestBeingEditted) return null

    function CloseButton({ position, rotation, scale }) {
      const [hover, setHover] = useState(false)
      const audioVolume = useAtomValue(audioVolumeAtom)
  
      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        setGuestBeingEditted(null)
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }
  
      return <group position={position} rotation={rotation} scale={scale}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[1.55, 0.01, 0.55]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[1.45, 0.02, 0.45]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='background-wrapper' 
          scale={[1.55, 0.02, 0.55]}
          onPointerEnter={e=>handlePointerEnter(e)}
          onPointerLeave={e=>handlePointerLeave(e)}
          onPointerDown={e=>handlePointerDown(e)}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Center position={[0,0.02,0]} rotation={[-Math.PI/2, 0, 0]} key={Math.random()}>
          <Text3D
            font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
            position={[-0.63,0.02,0.14]}
            size={0.26}
            height={0.01}
          >
            {translations.lobby.close[language]}
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </Text3D>
        </Center>
      </group>
    }
    function AssignHostButton({ position=[0,0,0] }) {
      const [hover, setHover] = useState(false);
      const audioVolume = useAtomValue(audioVolumeAtom)
      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        socket.emit('assignHost', { 
          roomId: params.id.toUpperCase(),
          clientId: client._id,
          userId: lastGuestBeingEditted._id,
          team: lastGuestBeingEditted.team,
          name: lastGuestBeingEditted.name
        }, (response) => {
          if (response === 'success') {
            setGuestBeingEditted(null)
          }
        })
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }
      return <group name='assign-host-button' position={position}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[8.5, 0.01, 1]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[8.4, 0.02, 0.9]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='wrapper'
            onPointerEnter={e => handlePointerEnter(e)}
            onPointerLeave={e => handlePointerLeave(e)}
            onPointerDown={e => handlePointerDown(e)}
            scale={[8.5, 0.02, 1]}
          >
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-4.05,0.02,0.2]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {translations.lobby.assignHost[language]}
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </Text3D>
      </group>
    }
    function KickButton({ position=[0,0,0] }) {
      const [hover, setHover] = useState(false);
      const audioVolume = useAtomValue(audioVolumeAtom)
      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        socket.emit('kick', { 
          roomId: params.id.toUpperCase(),
          clientId: client._id,
          team: lastGuestBeingEditted.team,
          name: lastGuestBeingEditted.name,
        }, (response) => {
          if (response === 'success') {
            setGuestBeingEditted(null)
          }
        })
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }
      return <group name='kick-button' position={position}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[8.5, 0.01, 1]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'red' }/>
          </mesh>
          <mesh name='background-inner' scale={[8.4, 0.02, 0.9]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='wrapper'
            onPointerEnter={e => handlePointerEnter(e)}
            onPointerLeave={e => handlePointerLeave(e)}
            onPointerDown={e => handlePointerDown(e)}
            scale={[8.5, 0.02, 1]}
          >
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-4.05,0.02,0.2]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {translations.lobby.kick[language]}
          <meshStandardMaterial color={ hover ? 'green' : 'red' }/>
        </Text3D>
      </group>
    }
    function SetSpectatorButton({ position=[0,0,0] }) {
      const [hover, setHover] = useState(false);
      const audioVolume = useAtomValue(audioVolumeAtom)
      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        // set player to spectator
        socket.emit('setTeam', {
          roomId: params.id.toUpperCase(),
          clientId: client._id,
          userId: lastGuestBeingEditted._id,
          name: lastGuestBeingEditted.name,
          currTeamId: lastGuestBeingEditted.team,
          newTeamId: -1
        }, (response) => {
          if (response === 'success') {
            setGuestBeingEditted(null)
          }
        })
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }
      return <group name='set-spectator-button' position={position}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[8.5, 0.01, 1]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[8.4, 0.02, 0.9]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='wrapper'
            onPointerEnter={e => handlePointerEnter(e)}
            onPointerLeave={e => handlePointerLeave(e)}
            onPointerDown={e => handlePointerDown(e)}
            scale={[8.5, 0.02, 1]}
          >
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black' transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-4.05,0.02,0.2]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {translations.lobby.setSpectator[language]}
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </Text3D>
      </group>
    }
    function handlePointerEnter(e) {
      e.stopPropagation()
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
    }
    
    // position={[0, 0.02, (-0.8) + (1 + 0.1) * 0]}
    // position={[0, 0.02, (-0.8) + (1 + 0.1) * 1]}
    // position={[0, 0.02, (-0.8) + (1 + 0.1) * 2]}
    const seatModalButtons = []
    if (lastGuestBeingEditted.type === 'ai') {
      seatModalButtons.push(<KickButton/>)
    } else {
      seatModalButtons.push(<SetSpectatorButton/>)
      seatModalButtons.push(<AssignHostButton/>)
      seatModalButtons.push(<KickButton/>)
    }
    const backgroundDimensions = {
      position: [0, 0, 0.1 + 0.5 * (seatModalButtons.length - 3.5)],
      scaleOuter: [9, 0.01, 1 * seatModalButtons.length + 1],
      scaleInner: [8.9, 0.02, 1 * seatModalButtons.length + 0.9]
    }

    return <group position={position} scale={scale}>
      {/* background */}
      <group name='background' position={backgroundDimensions.position}
        onPointerEnter={e=>handlePointerEnter(e)}
        onPointerLeave={e=>handlePointerLeave(e)}
      >
        <mesh name='background-outer' scale={backgroundDimensions.scaleOuter}>
          <boxGeometry args={[1,1,1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh>
        <mesh name='background-inner' scale={backgroundDimensions.scaleInner}>
          <boxGeometry args={[1,1,1]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
      </group>
      {/* title */}
      <group name='title' position={[0.34, 0.02, -2.2]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-4.6,0,0.7]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {translations.lobby.edit[language]}
          <meshStandardMaterial color='orange'/>
        </Text3D>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-3.2,0,0.7]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {lastGuestBeingEditted.name}
          <meshStandardMaterial color={mapTeamToPlayerColor(lastGuestBeingEditted.team)}/>
        </Text3D>
      </group>
      {/* navigation */}
      <CloseButton position={[3.5, 0.02, -1.725]}/>
      {/* action buttons */}
      { seatModalButtons.map((value, index) => {
        return <group position={[0, 0.02, 1 * index-0.8]} key={index}>
          {value}
        </group>
      }) }
    </group>
  }

  function SeatModal({ position, rotation, scale }) {
    const [lastSeatChosen, setLastSeatChosen] = useState(seatChosen)
    useEffect(() => {
      if (seatChosen) setLastSeatChosen(seatChosen)
    }, [seatChosen])

    if (!lastSeatChosen) return null

    function CloseButton({ position, rotation, scale }) {
      const [hover, setHover] = useState(false)
      const audioVolume = useAtomValue(audioVolumeAtom)

      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        setSeatChosen(null)
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }

      return <group position={position} rotation={rotation} scale={scale}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[1.55, 0.01, 0.55]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[1.45, 0.02, 0.45]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='background-wrapper' 
          scale={[1.55, 0.02, 0.55]}
          onPointerEnter={e=>handlePointerEnter(e)}
          onPointerLeave={e=>handlePointerLeave(e)}
          onPointerDown={e=>handlePointerDown(e)}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-0.63,0.02,0.14]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.26}
          height={0.01}
        >
          {translations.lobby.close[language]}
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </Text3D>
      </group>
    }
    function TakeSeatButton({ position, rotation, scale }) {
      const [hover, setHover] = useState(false)
      const audioVolume = useAtomValue(audioVolumeAtom)

      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      function handlePointerDown(e) {
        e.stopPropagation()
        setJoinTeam(lastSeatChosen[0])
        setSeatChosen(null)
        playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
      }

      return <group position={position} rotation={rotation} scale={scale}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[6.8, 0.01, 0.9]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[6.7, 0.02, 0.8]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='background-wrapper' 
          scale={[6.8, 0.02, 0.9]}
          onPointerEnter={e=>handlePointerEnter(e)}
          onPointerLeave={e=>handlePointerLeave(e)}
          onPointerDown={e=>handlePointerDown(e)}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-3.15,0.02,0.2]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {translations.lobby.takeSeat[language]}
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </Text3D>
      </group>
    }
    function AddAISmartButton({ position, rotation, scale }) {
      const [hover, setHover] = useState(false)
      const audioVolume = useAtomValue(audioVolumeAtom)

      function handlePointerEnter(e) {
        e.stopPropagation()
        setHover(true)
        document.body.style.cursor = 'pointer'
      }
      function handlePointerLeave(e) {
        e.stopPropagation()
        setHover(false)
        document.body.style.cursor = 'default'
      }
      async function handlePointerDown (e) {
        e.stopPropagation()
        setSeatChosen(null)
        // send 'add AI' event to server
        // must be host
        socket.emit('addAI', { roomId: params.id.toUpperCase(), clientId: client._id, team: lastSeatChosen[0], level: 'smart' })
      
        await trackEvent({ surface: 'lobby', action: 'add_ai_smart', category: 'gameplay' })
        
        playSoundEffect('/sounds/effects/add-ai-player.mp3', audioVolume)
      }


      return <group position={position} rotation={rotation} scale={scale}>
        {/* background */}
        <group name='background'>
          <mesh name='background-outer' scale={[6.8, 0.01, 0.9]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
          </mesh>
          <mesh name='background-inner' scale={[6.7, 0.02, 0.8]}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial color='black'/>
          </mesh>
          <mesh name='background-wrapper' 
          scale={[6.8, 0.02, 0.9]}
          onPointerEnter={e=>handlePointerEnter(e)}
          onPointerLeave={e=>handlePointerLeave(e)}
          onPointerDown={e=>handlePointerDown(e)}>
            <boxGeometry args={[1,1,1]}/>
            <meshStandardMaterial transparent opacity={0}/>
          </mesh>
        </group>
        {/* text */}
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          position={[-3.15,0.02,0.2]}
          rotation={[-Math.PI/2, 0, 0]}
          size={0.45}
          height={0.01}
        >
          {translations.lobby.addAI[language]}
          <meshStandardMaterial color={ hover ? 'green' : 'orange' }/>
        </Text3D>
      </group>
    }
    const seatModalButtons = [
      <TakeSeatButton/>,
      // <AddAIEZButton/>,
      <AddAISmartButton/>,
    ]
    const backgroundDimensions = {
      position: [0, 0, 0.1 + 0.5 * (seatModalButtons.length - 2)],
      scaleOuter: [7, 0.01, 1 * seatModalButtons.length + 1],
      scaleInner: [6.9, 0.02, 1 * seatModalButtons.length + 0.9]
    }

    return <group position={position} rotation={rotation} scale={scale}>
      {/* background */}
      <group name='background' position={backgroundDimensions.position}>
        <mesh name='background-outer' scale={backgroundDimensions.scaleOuter}>
        {/* <mesh name='background-outer' scale={[7, 0.01, 4.2]}> */}
          <boxGeometry args={[1,1,1]}/>
          <meshStandardMaterial color='orange'/>
        </mesh>
        <mesh name='background-inner' scale={backgroundDimensions.scaleInner}>
        {/* <mesh name='background-inner' scale={[6.9, 0.02, 4.1]}> */}
          <boxGeometry args={[1,1,1]}/>
          <meshStandardMaterial color='black'/>
        </mesh>
      </group>
      {/* title */}
      <Text3D
        font="/fonts/Luckiest Guy_Regular.json"
        position={[-3.3, 0.02, -0.7]}
        rotation={[-Math.PI/2, 0, 0]}
        size={0.45}
        height={0.01}
      >
        {`SEAT ${lastSeatChosen[1]+1}`}
        <meshStandardMaterial color={ lastSeatChosen[0] === 0 ? 'red' : 'turquoise' }/>
      </Text3D>
      {/* close button */}
      <CloseButton position={[2.45, 0.02, -0.95]} scale={1.2}/>
      { seatModalButtons.map((value, index) => {
        return <group position={[0, 0.02, 1 * index]} key={index}>
          {value}
        </group>
      }) }
    </group>
  }

  return <group scale={scale} position={position}>
    <BlueMoon scale={3.7} rotationSpeed={-0.2} position={[0, 0, 2]} opacity={1} surfaceBrightness={0.18} surfaceTint='#47bfff'/>
    <animated.group scale={partyScale}>
      <group ref={partyRef} scale={1.6} position={[0, 2, 4.3]}>
        <LobbyTeamTitle team={0} position={[-3.2, 5, -3]} rightEdge={-0.45} />
        <LobbyTeamTitle team={1} position={[0.6, 5, -3]} />
        <YootDisplay scale={0.2} position={[-0.15, 5, 1.1]} rotation={[0, Math.PI/2, 0]}/>
        <Seats position={[0, 0, 1]} scale={1}/>
      </group>
      { device === 'portrait' && shipRefs.map((value, index) => {
        return (index < 4 ? <group ref={value} key={index}>
          <Rocket scale={1}/>
        </group> : <group ref={value} key={index}>
          <Ufo scale={1}/>
        </group>)
      })}
    </animated.group>
    {/* <SeatStatus/> */}
    <animated.group scale={joinTeamScale}>
      <JoinTeamModal 
        position={layout[device].lobby.joinTeamModal.position}
        rotation={layout[device].lobby.joinTeamModal.rotation}
        scale={layout[device].lobby.joinTeamModal.scale}
      />
    </animated.group>
    <animated.group scale={teamNameScale}>
      {editingTeamName !== null && <group position={[-4.5, 12, 4.2]} rotation={[-Math.PI/2, 0, 0]} scale={1.6}>
        <TeamNameModal team={editingTeamName} name={teams[editingTeamName].name || (editingTeamName === 0 ? translations.lobby.teamRocket[language] : translations.lobby.teamUfo[language])} onClose={() => setEditingTeamName(null)} />
      </group>}
    </animated.group>
    <animated.group scale={playerNameScale}>
      {editingPlayerName && <group position={[-6.6, 12, 3]} rotation={[-Math.PI/2, 0, 0]} scale={1.6}>
        <PlayerNameModal name={client.name} onClose={() => setEditingPlayerName(false)} />
      </group>}
    </animated.group>
    <animated.group scale={seatScale}>
      <SeatModal position={[0, 13, 7]} rotation={[0,0,0]} scale={1.5}/>
    </animated.group>
    <animated.group scale={editGuestScale}>
      <EditOneGuest 
        position={[0,12,7]} 
        scale={1.3}
        guestBeingEditted={guestBeingEditted}
      />
    </animated.group>
    <LanguageModalAnimated />
  </group>
}
